import React, { useMemo } from 'react';
import {
  CircleF,
  GoogleMap,
  MarkerF,
  PolylineF,
  useJsApiLoader,
} from '@react-google-maps/api';
import { SVGS } from '../assets';
import './map-component.scss';
import { getConfigValue } from '@zonofi/common/functions/get-config-value';

interface MapComponentProps {
  isAvailable: boolean;
  center: {
    lat: number;
    lng: number;
  };
  radius: number;
  className?: string;
  onLoad?: (map: google.maps.Map) => void;
  onUnmount?: () => void;
  onDragEnd?: () => void;
  requireRadius?: boolean;
}

const GOOGLE_MAP_API_KEY = getConfigValue('GOOGLE_MAP_API_KEY');

function generateCirclePath(
  center: { lat: number; lng: number },
  radiusInMeters: number,
  numPoints = 360
): { lat: number; lng: number }[] {
  const points: { lat: number; lng: number }[] = [];
  const earthRadius = 6371000;

  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const latOffset = (radiusInMeters / earthRadius) * (180 / Math.PI);
    const lngOffset =
      (radiusInMeters /
        (earthRadius * Math.cos((center.lat * Math.PI) / 180))) *
      (180 / Math.PI);

    points.push({
      lat: center.lat + latOffset * Math.sin(angle),
      lng: center.lng + lngOffset * Math.cos(angle),
    });
  }

  return points;
}

export const MapComponent: React.FC<MapComponentProps> = ({
  isAvailable,
  center,
  radius,
  className = '',
  onLoad,
  onUnmount,
  onDragEnd,
  requireRadius = false,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const circlePath = useMemo(() => {
    if (!center?.lat || !center?.lng) return [];
    return generateCirclePath(center, radius);
  }, [center, radius]);

  return (
    <div className={`check-in__map-view ${className}`}>
      {isAvailable && isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          zoom={16}
          center={center}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: false,
            disableDefaultUI: true,
          }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={onDragEnd}
        >
          <MarkerF
            position={center}
            icon={{
              url: SVGS.GpsLocation,
              scaledSize: new window.google.maps.Size(25, 25),
            }}
          />
          {requireRadius && (
            <>
              <PolylineF
                path={circlePath}
                options={{
                  strokeOpacity: 0,
                  strokeColor: '#2ABB7F',
                  icons: [
                    {
                      icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 1,
                        strokeColor: '#2ABB7F',
                        strokeWeight: 2,
                        scale: 3,
                      },
                      offset: '0',
                      repeat: '14px',
                    },
                  ],
                }}
              />
              <CircleF
                center={center}
                radius={radius}
                options={{
                  fillColor: '#2ABB7F',
                  fillOpacity: 0.15,
                  strokeWeight: 0,
                }}
              />
            </>
          )}
        </GoogleMap>
      )}
    </div>
  );
};
